import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchSessions, forceLogoutChain, forceLogoutUser, type SessionChain } from '../api/sessions';
import { usePermission } from '../hooks/usePermission';
import { useConfirm } from '../context/ConfirmDialogContext';
import { useSnackbar } from '../context/SnackbarContext';

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function Sessions() {
  const qc = useQueryClient();
  const canRead = usePermission('auth.sessions.read');
  const canManage = usePermission('auth.sessions.manage');
  const confirm = useConfirm();
  const showSnackbar = useSnackbar();

  const { data: sessions, isLoading, error } = useQuery({
    queryKey: ['auth-sessions'],
    queryFn: fetchSessions,
    enabled: canRead,
    refetchInterval: 30_000,
  });

  const logoutChainMutation = useMutation({
    mutationFn: (chainId: string) => forceLogoutChain(chainId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth-sessions'] }),
  });

  const logoutAllMutation = useMutation({
    mutationFn: (username: string) => forceLogoutUser(username),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth-sessions'] }),
  });

  async function handleLogoutChain(chainId: string, username: string) {
    const ok = await confirm(`Force-logout this session for "${username}"?`, {
      title: 'Force Logout Session',
      confirmLabel: 'Force Logout',
      danger: true,
    });
    if (!ok) return;
    try {
      await logoutChainMutation.mutateAsync(chainId);
      showSnackbar('Session revoked', 'success');
    } catch {
      showSnackbar('Failed to revoke session', 'error');
    }
  }

  async function handleLogoutAll(username: string) {
    const ok = await confirm(`Force-logout ALL sessions for "${username}"?`, {
      title: 'Logout All Sessions',
      confirmLabel: 'Logout All',
      danger: true,
    });
    if (!ok) return;
    try {
      await logoutAllMutation.mutateAsync(username);
      showSnackbar(`All sessions for "${username}" revoked`, 'success');
    } catch {
      showSnackbar('Failed to revoke sessions', 'error');
    }
  }

  const columns: GridColDef<SessionChain>[] = [
    {
      field: 'username',
      headerName: 'User',
      width: 130,
    },
    {
      field: 'started_at',
      headerName: 'Started',
      width: 175,
      valueGetter: (_v, row) => new Date(row.started_at * 1000).toLocaleString(),
    },
    {
      field: 'last_seen',
      headerName: 'Last Seen',
      width: 175,
      valueGetter: (_v, row) => new Date(row.last_seen * 1000).toLocaleString(),
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 90,
      sortable: false,
      renderCell: (p: GridRenderCellParams<SessionChain>) => {
        const secs = p.row.last_seen - p.row.started_at;
        return <Typography variant="body2">{formatDuration(secs)}</Typography>;
      },
    },
    {
      field: 'refresh_count',
      headerName: 'Refreshes',
      width: 90,
      renderCell: (p: GridRenderCellParams<SessionChain>) =>
        p.row.refresh_count > 0 ? (
          <Chip label={p.row.refresh_count} size="small" variant="outlined" />
        ) : null,
    },
    {
      field: 'ip',
      headerName: 'IP',
      width: 140,
      renderCell: (p: GridRenderCellParams<SessionChain>) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value as string}</Typography>
      ),
    },
    {
      field: 'user_agent',
      headerName: 'User-Agent',
      flex: 1,
      minWidth: 200,
      renderCell: (p: GridRenderCellParams<SessionChain>) => (
        <Tooltip title={p.value as string}>
          <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>{p.value as string}</Typography>
        </Tooltip>
      ),
    },
    ...(canManage
      ? [
          {
            field: 'actions',
            headerName: 'Actions',
            width: 240,
            sortable: false,
            renderCell: (p: GridRenderCellParams<SessionChain>) => (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  color="warning"
                  startIcon={<LogoutIcon />}
                  onClick={() => handleLogoutChain(p.row.chain_id, p.row.username)}
                  disabled={logoutChainMutation.isPending || logoutAllMutation.isPending}
                >
                  Force logout
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<PersonOffIcon />}
                  onClick={() => handleLogoutAll(p.row.username)}
                  disabled={logoutChainMutation.isPending || logoutAllMutation.isPending}
                >
                  Logout all
                </Button>
              </Stack>
            ),
          } as GridColDef<SessionChain>,
        ]
      : []),
  ];

  if (!canRead) {
    return <Typography color="error">You do not have permission to view sessions.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Active Sessions</Typography>

      {isLoading && <CircularProgress />}
      {error && <Typography color="error">Failed to load sessions</Typography>}
      {sessions && sessions.length === 0 && (
        <Typography color="text.secondary">No active sessions.</Typography>
      )}
      {sessions && sessions.length > 0 && (
        <DataGrid
          rows={sessions}
          columns={columns}
          getRowId={(r) => r.chain_id}
          density="compact"
          disableRowSelectionOnClick
          pageSizeOptions={[25]}
          sx={{ bgcolor: 'background.paper' }}
        />
      )}
    </Box>
  );
}
